import { useMemo, useState } from "react";
import { Button, Empty, Space, Tag, Tooltip, Typography, theme } from "antd";
import {
  BranchesOutlined,
  ExclamationCircleFilled,
  HighlightOutlined,
  HolderOutlined,
  PlusOutlined,
  WarningFilled,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  closestCorners,
  useDroppable,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
  type UniqueIdentifier,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import type { ProcessSection, ProcessStatus, ProcessWorkflow } from "../../api";
import type { FieldRef, Problem, Selection, StageDraft } from "./types";
import { problemsFor, worstOf } from "./validation";
import { describeCondition, isConditional } from "./conditionText";
import { statusByKind, statusPerStage } from "./lifecycle";

const { Text } = Typography;

/* Drag ids are prefixed so one DndContext can carry three different kinds of
   move (stage reorder, section move, sign-off move) without ambiguity. */
const STAGE = "S#";
const ZONE = "Z#";
const FORM = "F#";
const WORKFLOW = "W#";
const TRAY = `${ZONE}__tray__`;

export type ItemKind = "form" | "workflow";

/* Every node below lives at module scope on purpose. Declaring them inside the
   parent would give React a brand new component type on each render, which
   remounts the whole tree — and the first remount happens the instant a drag
   starts and sets state, so the drag would be cancelled before it began. */

/** The badge a node wears when something about it is wrong. */
const ProblemMark = ({ problems }: { problems: Problem[] }) => {
  const { t } = useTranslation();
  const { token } = theme.useToken();
  const worst = worstOf(problems);
  if (!worst) return null;
  return (
    <Tooltip
      title={
        <div>
          {problems.map((p, i) => (
            <div key={i}>{t(p.message, p.params)}</div>
          ))}
        </div>
      }
    >
      {worst === "error" ? (
        <ExclamationCircleFilled style={{ color: token.colorError }} />
      ) : (
        <WarningFilled style={{ color: token.colorWarning }} />
      )}
    </Tooltip>
  );
};

/** A draggable, selectable line in the outline. */
const OutlineRow = ({
  dragId,
  selected,
  indent,
  onClick,
  handleLabel,
  children,
}: {
  dragId: string;
  selected: boolean;
  indent?: boolean;
  onClick: () => void;
  handleLabel: string;
  children: React.ReactNode;
}) => {
  const { token } = theme.useToken();
  const sortable = useSortable({ id: dragId });
  return (
    <div
      ref={sortable.setNodeRef}
      style={{
        transform: CSS.Translate.toString(sortable.transform),
        transition: sortable.transition,
        opacity: sortable.isDragging ? 0.4 : 1,
        display: "flex",
        alignItems: "center",
        gap: 6,
        padding: "4px 8px 4px 4px",
        marginLeft: indent ? 22 : 0,
        marginBottom: 2,
        borderRadius: token.borderRadius,
        cursor: "pointer",
        background: selected ? token.controlItemBgActive : undefined,
        boxShadow: selected ? `inset 2px 0 0 ${token.colorPrimary}` : undefined,
      }}
      onClick={onClick}
    >
      <span
        {...sortable.attributes}
        {...sortable.listeners}
        style={{
          cursor: "grab",
          color: token.colorTextQuaternary,
          lineHeight: 1,
          touchAction: "none",
        }}
        aria-label={handleLabel}
        onClick={(e) => e.stopPropagation()}
      >
        <HolderOutlined />
      </span>
      {children}
    </div>
  );
};

const SectionRow = ({
  section,
  selected,
  problems,
  onSelect,
}: {
  section: ProcessSection;
  selected: boolean;
  problems: Problem[];
  onSelect: () => void;
}) => {
  const { t } = useTranslation();
  const { token } = theme.useToken();
  return (
    <OutlineRow
      dragId={`${FORM}${section.id}`}
      selected={selected}
      indent
      onClick={onSelect}
      handleLabel={t("Drag to move")}
    >
      <Text ellipsis style={{ flex: 1, fontSize: 13 }}>
        {t(section.title)}
      </Text>
      <ProblemMark problems={problems} />
      {section.signature_kind !== "None" && (
        <Tooltip title={t("This section is signed")}>
          <HighlightOutlined style={{ color: token.colorTextQuaternary }} />
        </Tooltip>
      )}
      {!section.is_required && (
        <Tag style={{ marginInlineEnd: 0 }}>{t("Optional")}</Tag>
      )}
    </OutlineRow>
  );
};

/** How a slot's approvers are decided, in one word. */
const ASSIGNMENT_SHORT: Record<string, string> = {
  ManualAny: "chosen",
  ManualFromPool: "from group",
  AutoQuorum: "quorum",
  AutoAll: "all",
};

const WorkflowRow = ({
  workflow,
  selected,
  problems,
  onSelect,
}: {
  workflow: ProcessWorkflow;
  selected: boolean;
  problems: Problem[];
  onSelect: () => void;
}) => {
  const { t } = useTranslation();
  const { token } = theme.useToken();
  const named = !!workflow.name?.trim();
  const roles = workflow.roles ?? [];

  return (
    <>
      <OutlineRow
        dragId={`${WORKFLOW}${workflow.id}`}
        selected={selected}
        indent
        onClick={onSelect}
        handleLabel={t("Drag to move")}
      >
        <Text
          ellipsis
          type={named ? undefined : "danger"}
          style={{ flex: 1, fontSize: 13 }}
        >
          {named ? t(workflow.name) : t("Untitled sign-off")}
        </Text>
        <ProblemMark problems={problems} />
        {roles.length === 0 && (
          <Text type="secondary" style={{ fontSize: 12 }}>
            {t("{{count}} approvers", { count: workflow.approvers_count })}
          </Text>
        )}
      </OutlineRow>

      {/* Who signs, and how each signer is decided, is the process for a
          permit-to-work. Burying it one selection deep made the outline look
          like it was hiding the important half. */}
      {roles.map((role, i) => (
        <div
          key={role.id ?? `new-${i}`}
          onClick={onSelect}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            cursor: "pointer",
            padding: "1px 8px 1px 0",
            marginLeft: 48,
          }}
        >
          <span
            aria-hidden
            style={{ color: token.colorTextQuaternary, fontSize: 11 }}
          >
            └
          </span>
          <Text
            ellipsis
            type={role.title?.trim() ? "secondary" : "danger"}
            style={{ flex: 1, fontSize: 12 }}
          >
            {role.title?.trim() || t("Unnamed slot")}
          </Text>
          <Text type="secondary" style={{ fontSize: 11 }}>
            {t(ASSIGNMENT_SHORT[role.assignment] ?? role.assignment)}
            {role.assignment === "AutoQuorum" ? ` ${role.quorum}` : ""}
          </Text>
        </div>
      ))}
    </>
  );
};

const StageNode = ({
  stage,
  index,
  sections,
  workflowById,
  selection,
  problems,
  simulation,
  conditionText,
  onSelect,
}: {
  stage: StageDraft;
  index: number;
  sections: Record<number, ProcessSection>;
  workflowById: Record<number, ProcessWorkflow>;
  selection: Selection;
  problems: Problem[];
  /** Null when nothing has been simulated; false means this stage is skipped. */
  simulation: boolean | null;
  /** The stage's rule in words, empty when it always runs. */
  conditionText: string;
  onSelect: (selection: Selection) => void;
}) => {
  const { t } = useTranslation();
  const { token } = theme.useToken();
  const sortable = useSortable({ id: `${STAGE}${stage.key}` });
  const drop = useDroppable({ id: `${ZONE}${stage.key}` });

  const isEntry = stage.kind === "DataEntry";
  const children = isEntry ? stage.form_ids : stage.workflow_ids;
  const selected =
    selection?.type === "stage" && selection.key === stage.key;
  const conditional = Object.keys(stage.condition ?? {}).length > 0;
  // Blue = people type things in, violet = people sign things off.
  const accent = isEntry ? token.colorPrimary : token.purple6;
  const skipped = simulation === false;

  return (
    <div
      ref={sortable.setNodeRef}
      style={{
        transform: CSS.Translate.toString(sortable.transform),
        transition: sortable.transition,
        opacity: sortable.isDragging ? 0.4 : skipped ? 0.45 : 1,
        marginBottom: 4,
      }}
    >
      <div
        onClick={() => onSelect({ type: "stage", key: stage.key })}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          padding: "6px 8px 6px 4px",
          borderRadius: token.borderRadius,
          cursor: "pointer",
          background: selected ? token.controlItemBgActive : undefined,
          boxShadow: selected ? `inset 2px 0 0 ${accent}` : undefined,
        }}
      >
        <span
          {...sortable.attributes}
          {...sortable.listeners}
          style={{
            cursor: "grab",
            color: token.colorTextQuaternary,
            lineHeight: 1,
            touchAction: "none",
          }}
          aria-label={t("Drag to reorder this step")}
          onClick={(e) => e.stopPropagation()}
        >
          <HolderOutlined />
        </span>
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            width: 20,
            height: 20,
            flex: "0 0 auto",
            borderRadius: "50%",
            background: accent,
            color: "#fff",
            fontSize: 11,
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {index + 1}
        </span>
        <Text
          strong
          ellipsis
          type={stage.name.trim() ? undefined : "danger"}
          style={{
            flex: 1,
            textDecoration: skipped ? "line-through" : undefined,
          }}
        >
          {stage.name.trim() || t("Name this step")}
        </Text>
        {skipped && <Tag style={{ marginInlineEnd: 0 }}>{t("Skipped")}</Tag>}
        <ProblemMark problems={problems} />
        {conditional && !conditionText && (
          <Tooltip title={t("This step only applies sometimes")}>
            <BranchesOutlined style={{ color: token.colorWarning }} />
          </Tooltip>
        )}
        <Text type="secondary" style={{ fontSize: 12 }}>
          {children.length}
        </Text>
      </div>

      {/* The rule itself, not just a hint that there is one. Reading it used to
          mean selecting the stage and opening the condition builder. */}
      {conditionText && (
        <Tooltip title={conditionText}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 4,
              margin: "0 0 2px 32px",
              color: token.colorWarningText,
              fontSize: 11,
            }}
          >
            <BranchesOutlined />
            <span
              style={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {conditionText}
            </span>
          </div>
        </Tooltip>
      )}

      {/* The spine: children hang off a rule that lights up when you drag over
          it, so an empty stage is still an obvious drop target. */}
      <div
        ref={drop.setNodeRef}
        style={{
          marginLeft: 13,
          paddingLeft: 8,
          paddingTop: 2,
          paddingBottom: 2,
          minHeight: 12,
          borderLeft: `2px solid ${
            drop.isOver ? accent : token.colorBorderSecondary
          }`,
        }}
      >
        <SortableContext
          items={children.map((c) => `${isEntry ? FORM : WORKFLOW}${c}`)}
          strategy={verticalListSortingStrategy}
        >
          {children.length === 0 ? (
            <Text
              type="secondary"
              style={{ fontSize: 12, display: "block", padding: "2px 0 2px 22px" }}
            >
              {isEntry ? t("Drop a section here") : t("Drop a sign-off here")}
            </Text>
          ) : isEntry ? (
            children.map((formId) =>
              sections[formId] ? (
                <SectionRow
                  key={formId}
                  section={sections[formId]}
                  selected={
                    selection?.type === "section" && selection.id === formId
                  }
                  problems={problemsFor(problems, { type: "section", id: formId })}
                  onSelect={() => onSelect({ type: "section", id: formId })}
                />
              ) : null
            )
          ) : (
            children.map((workflowId) =>
              workflowById[workflowId] ? (
                <WorkflowRow
                  key={workflowId}
                  workflow={workflowById[workflowId]}
                  selected={
                    selection?.type === "workflow" && selection.id === workflowId
                  }
                  problems={problemsFor(problems, {
                    type: "workflow",
                    id: workflowId,
                  })}
                  onSelect={() => onSelect({ type: "workflow", id: workflowId })}
                />
              ) : null
            )
          )}
        </SortableContext>
      </div>
    </div>
  );
};

const StatusChip = ({
  status,
  onOpen,
}: {
  status: ProcessStatus;
  onOpen: () => void;
}) => {
  const { t } = useTranslation();
  const { token } = theme.useToken();
  return (
    <Tooltip title={t("Edit the lifecycle")}>
      <div
        role="button"
        tabIndex={0}
        onClick={onOpen}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onOpen();
          }
        }}
        style={{
          display: "inline-flex",
          alignItems: "center",
          padding: "3px 10px",
          margin: "2px 0 2px 4px",
          borderRadius: 999,
          cursor: "pointer",
          border: `1px dashed ${token.colorBorder}`,
        }}
      >
        <Text type="secondary" style={{ fontSize: 12 }}>
          {t(status.name)}
        </Text>
      </div>
    </Tooltip>
  );
};

/**
 * The status a permit sits in while it walks a run of stages.
 *
 * The lifecycle and the stages are one machine — a permit in an Approval stage
 * is in the Approval status — but the designer used to present them as two
 * unrelated lists, which is where most of the confusion about this engine
 * starts. Banding the spine by status says it without a paragraph of prose.
 */
const StatusBand = ({
  statusName,
  kind,
  onOpen,
}: {
  statusName?: string;
  kind: string;
  onOpen: () => void;
}) => {
  const { t } = useTranslation();
  const { token } = theme.useToken();
  if (!statusName) return null;
  const accent = kind === "DataEntry" ? token.colorPrimary : token.purple6;
  return (
    <Tooltip title={t("Edit the lifecycle")}>
      <div
        role="button"
        tabIndex={0}
        onClick={onOpen}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onOpen();
          }
        }}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 6,
          margin: "10px 0 4px",
          cursor: "pointer",
        }}
      >
        <span
          style={{
            width: 3,
            height: 12,
            borderRadius: 2,
            background: accent,
            flex: "0 0 auto",
          }}
        />
        <Text type="secondary" style={{ fontSize: 11, letterSpacing: 0.4 }}>
          {t("While in {{status}}", { status: t(statusName) }).toUpperCase()}
        </Text>
      </div>
    </Tooltip>
  );
};

const Tray = ({
  looseSections,
  looseWorkflows,
  selection,
  problems,
  onSelect,
}: {
  looseSections: ProcessSection[];
  looseWorkflows: ProcessWorkflow[];
  selection: Selection;
  problems: Problem[];
  onSelect: (selection: Selection) => void;
}) => {
  const { t } = useTranslation();
  const { token } = theme.useToken();
  const drop = useDroppable({ id: TRAY });
  const empty = looseSections.length === 0 && looseWorkflows.length === 0;

  return (
    <div style={{ marginTop: 16 }}>
      <Text type="secondary" style={{ fontSize: 12, fontWeight: 500 }}>
        {t("Not in any step")}
      </Text>
      <div
        ref={drop.setNodeRef}
        style={{
          marginTop: 6,
          padding: 6,
          minHeight: 44,
          borderRadius: token.borderRadius,
          border: `1px dashed ${
            drop.isOver ? token.colorPrimary : token.colorBorder
          }`,
          background: drop.isOver ? token.controlItemBgHover : undefined,
        }}
      >
        {empty ? (
          <Text type="secondary" style={{ fontSize: 12 }}>
            {t("Everything is in a step. Drag something here to take it out.")}
          </Text>
        ) : (
          <SortableContext
            items={[
              ...looseSections.map((s) => `${FORM}${s.id}`),
              ...looseWorkflows.map((w) => `${WORKFLOW}${w.id}`),
            ]}
            strategy={verticalListSortingStrategy}
          >
            {looseSections.map((s) => (
              <SectionRow
                key={s.id}
                section={s}
                selected={selection?.type === "section" && selection.id === s.id}
                problems={problemsFor(problems, { type: "section", id: s.id })}
                onSelect={() => onSelect({ type: "section", id: s.id })}
              />
            ))}
            {looseWorkflows.map((w) => (
              <WorkflowRow
                key={w.id}
                workflow={w}
                selected={
                  selection?.type === "workflow" && selection.id === w.id
                }
                problems={problemsFor(problems, {
                  type: "workflow",
                  id: w.id as number,
                })}
                onSelect={() =>
                  onSelect({ type: "workflow", id: w.id as number })
                }
              />
            ))}
          </SortableContext>
        )}
      </div>
      {!empty && (
        <Text
          type="secondary"
          style={{ fontSize: 12, display: "block", marginTop: 6 }}
        >
          {t("A case never reaches these, because no step opens them.")}
        </Text>
      )}
    </div>
  );
};

type Props = {
  stages: StageDraft[];
  sections: Record<number, ProcessSection>;
  workflows: ProcessWorkflow[];
  statuses: ProcessStatus[];
  selection: Selection;
  onSelect: (selection: Selection) => void;
  /** Put an item in a stage (or nowhere) at a position. One call covers move,
   *  reorder and unassign, because to a user dragging they are one gesture. */
  onPlaceItem: (
    kind: ItemKind,
    itemId: number,
    toStageKey: string | null,
    index: number
  ) => void;
  onReorderStages: (from: number, to: number) => void;
  onAddStage: (kind: string) => void;
  /** The lifecycle needs a table wider than the inspector, so the endpoints of
   *  the spine open it rather than selecting into the side panel. */
  onOpenLifecycle: () => void;
  /** Every field in the template, for putting a stage's rule into words. */
  fields: FieldRef[];
  /** Everything wrong with the draft, so each node can wear its own badge. */
  problems: Problem[];
  /** Stage key -> whether it runs, from the last simulation. Empty when none
   *  has been run; the outline then shows the process unfiltered. */
  simulation: Record<string, boolean> | null;
};

/**
 * The process as one vertical spine: where a permit starts, every stage it
 * walks, and where it ends — with each stage's contents nested underneath.
 *
 * This is both the overview and the navigation. Selecting anything here decides
 * what the inspector edits, so there is one place that answers "what is in this
 * process?" and one place that answers "what does this piece do?". Moving a
 * section between stages is a drag, not a dropdown: it is a spatial change and
 * it should feel like one.
 */
const ProcessOutline = (props: Props) => {
  const { t } = useTranslation();
  const { token } = theme.useToken();
  const [dragging, setDragging] = useState<UniqueIdentifier | null>(null);

  const sensors = useSensors(
    // A few pixels of travel before a drag starts, so selecting by click still
    // works on the same element.
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const workflowById = useMemo(
    () =>
      Object.fromEntries(props.workflows.map((w) => [w.id, w])) as Record<
        number,
        ProcessWorkflow
      >,
    [props.workflows]
  );

  const placedForms = new Set(props.stages.flatMap((s) => s.form_ids));
  const placedWorkflows = new Set(props.stages.flatMap((s) => s.workflow_ids));
  const looseSections = Object.values(props.sections).filter(
    (s) => !placedForms.has(s.id)
  );
  const looseWorkflows = props.workflows.filter(
    (w) => w.id != null && !placedWorkflows.has(w.id)
  );

  const startStatus = statusByKind(props.statuses, "Draft");
  const endStatus =
    statusByKind(props.statuses, "Active") ??
    statusByKind(props.statuses, "Closed");

  // Which state each step runs under. This used to be decided by stage kind
  // alone -- data entry meant Draft -- which labels a closure record "while in
  // Draft" even though it runs long after approval. `statusPerStage` walks the
  // lifecycle instead, and the canvas draws its bands from the same function,
  // so the two views cannot disagree about the same process.
  const perStage = statusPerStage(props.stages, props.statuses);

  const runs: Array<{
    kind: string;
    statusName?: string;
    items: Array<{ stage: StageDraft; index: number }>;
  }> = [];
  props.stages.forEach((stage, index) => {
    const statusName = perStage[index]?.name;
    const last = runs[runs.length - 1];
    // A run breaks when either the kind or the state changes: the band labels
    // the state, and the heading colour follows the kind.
    if (last && last.kind === stage.kind && last.statusName === statusName) {
      last.items.push({ stage, index });
    } else {
      runs.push({ kind: stage.kind, statusName, items: [{ stage, index }] });
    }
  });

  /** Which stage holds an item right now — `null` means the unassigned tray. */
  const containerOf = (dragId: string): string | null => {
    const isForm = dragId.startsWith(FORM);
    const id = Number(dragId.slice(2));
    const stage = props.stages.find((s) =>
      (isForm ? s.form_ids : s.workflow_ids).includes(id)
    );
    return stage ? stage.key : null;
  };

  /** Resolve whatever the pointer is over to a target stage (or the tray). */
  const zoneOf = (overId: string): string | null | undefined => {
    if (overId === TRAY) return null;
    if (overId.startsWith(ZONE)) return overId.slice(ZONE.length);
    if (overId.startsWith(STAGE)) return overId.slice(STAGE.length);
    if (overId.startsWith(FORM) || overId.startsWith(WORKFLOW)) {
      return containerOf(overId);
    }
    return undefined;
  };

  const onDragEnd = (e: DragEndEvent) => {
    setDragging(null);
    const activeId = String(e.active.id);
    const overId = e.over ? String(e.over.id) : "";
    if (!overId || activeId === overId) return;

    // Reordering the stages themselves.
    if (activeId.startsWith(STAGE)) {
      if (!overId.startsWith(STAGE)) return;
      const from = props.stages.findIndex(
        (s) => s.key === activeId.slice(STAGE.length)
      );
      const to = props.stages.findIndex(
        (s) => s.key === overId.slice(STAGE.length)
      );
      if (from >= 0 && to >= 0 && from !== to) props.onReorderStages(from, to);
      return;
    }

    // Moving a section or a sign-off.
    const isForm = activeId.startsWith(FORM);
    if (!isForm && !activeId.startsWith(WORKFLOW)) return;
    const target = zoneOf(overId);
    if (target === undefined) return;

    // Dropped onto a sibling: land where that sibling is. Dropped onto the
    // stage itself or its empty space: land at the end.
    let index = -1;
    if (overId.startsWith(FORM) || overId.startsWith(WORKFLOW)) {
      const stage = props.stages.find((s) => s.key === target);
      const list: number[] = stage
        ? isForm
          ? stage.form_ids
          : stage.workflow_ids
        : (isForm ? looseSections : looseWorkflows).map((x) => x.id as number);
      index = list.indexOf(Number(overId.slice(2)));
    }
    props.onPlaceItem(
      isForm ? "form" : "workflow",
      Number(activeId.slice(2)),
      target,
      index
    );
  };

  /** What follows the cursor while dragging. */
  const overlayLabel = (): string => {
    const dragId = String(dragging ?? "");
    if (dragId.startsWith(FORM)) {
      return props.sections[Number(dragId.slice(2))]?.title ?? "";
    }
    if (dragId.startsWith(WORKFLOW)) {
      return workflowById[Number(dragId.slice(2))]?.name ?? "";
    }
    if (dragId.startsWith(STAGE)) {
      return (
        props.stages.find((s) => s.key === dragId.slice(STAGE.length))?.name ?? ""
      );
    }
    return "";
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCorners}
      onDragStart={(e: DragStartEvent) => setDragging(e.active.id)}
      onDragCancel={() => setDragging(null)}
      onDragEnd={onDragEnd}
    >
      {startStatus && (
        <StatusChip status={startStatus} onOpen={props.onOpenLifecycle} />
      )}

      {props.stages.length === 0 ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          style={{ margin: "16px 0" }}
          description={t(
            "No stages yet. Every section is open at once and sign-offs run in their own order."
          )}
        />
      ) : (
        <SortableContext
          items={props.stages.map((s) => `${STAGE}${s.key}`)}
          strategy={verticalListSortingStrategy}
        >
          {runs.map((run, runIndex) => (
            <div key={`${run.kind}-${runIndex}`}>
              <StatusBand
                statusName={run.statusName}
                kind={run.kind}
                onOpen={props.onOpenLifecycle}
              />
              {run.items.map(({ stage, index }) => (
                <StageNode
                  key={stage.key}
                  stage={stage}
                  index={index}
                  sections={props.sections}
                  workflowById={workflowById}
                  selection={props.selection}
                  problems={problemsFor(props.problems, {
                    type: "stage",
                    key: stage.key,
                  })}
                  simulation={
                    props.simulation ? props.simulation[stage.key] ?? true : null
                  }
                  conditionText={
                    isConditional(stage.condition)
                      ? describeCondition(stage.condition, props.fields, t as any)
                      : ""
                  }
                  onSelect={props.onSelect}
                />
              ))}
            </div>
          ))}
        </SortableContext>
      )}

      {endStatus && (
        <StatusChip status={endStatus} onOpen={props.onOpenLifecycle} />
      )}

      <Space size={4} style={{ marginTop: 12 }} wrap>
        <Button
          size="small"
          icon={<PlusOutlined />}
          onClick={() => props.onAddStage("DataEntry")}
        >
          {t("Fill in")}
        </Button>
        <Button
          size="small"
          icon={<PlusOutlined />}
          onClick={() => props.onAddStage("Approval")}
        >
          {t("Sign off")}
        </Button>
      </Space>

      <Tray
        looseSections={looseSections}
        looseWorkflows={looseWorkflows}
        selection={props.selection}
        problems={props.problems}
        onSelect={props.onSelect}
      />

      <DragOverlay>
        {dragging ? (
          <div
            style={{
              padding: "4px 10px",
              borderRadius: token.borderRadius,
              background: token.colorBgElevated,
              boxShadow: token.boxShadowSecondary,
              fontSize: 13,
            }}
          >
            {overlayLabel()}
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
};

export default ProcessOutline;
